import $ from 'jquery'
import req from '../../lib/authRequest'
import Cookies from 'js-cookie'
import * as types from '../mutation-types'
import { reject } from 'underscore'
// Vue.config.devtools = true

/**
 * A User Vuex module
 */

// initial state
const state = {
  username: '',
  cid: '',
  perms: {},
  siteAdmin: false,
  loggedIn: undefined,
  info: {
    name: '',
    first_name: '',
    middle_initial: '',
    last_name: '',
    business: false,
    dob: '',
    phone: '',
    email: ''
  },
  user_loaded: false,
  connected_users: [],
  editing_user: {
    created: '',
    username: ''
  },
  editing_user_permissions: {},
  perm_categories: {},
  perm_schema: {},
  // Stored request information when refreshing an access token
  ajaxCallback: undefined,
  ajaxOptions: {},
  passwordWordList: [],
  primary_admin: false,
  hasDarkTheme: localStorage.getItem('xm-theme') ? localStorage.getItem('xm-theme') === 'dark' : null,
  service_address: {line1: '', line2: '', city: '', state: '', zip_code: '', fname: '', lname: '', mnami: ''},
}

const helpers = {
  hasPermission (perms, category, permission) {
    if ('admin' in perms && perms['admin']['has_manage']) {
      return true
    }
    // Sanity check that the category exists
    if (!(category in perms)) {
      return false
    }
    // Check if the manage permission exists for this category if requesting a view
    if (permission === 'view' && perms[category]['has_manage']) {
      return true
    }
    let permissionName = 'has_' + permission
    // Sanity check that the permission exists
    if (!(permissionName in perms[category])) {
      return false
    }
    return perms[category][permissionName]
  },
  hasOnePermission (perms, permsRequired) {
    // Return true if admin
    if ('admin' in perms && perms['admin']['has_manage']) {
      return true
    }
    // Check that at least one of the perms is in permsRequired
    for (var category in permsRequired) {
      let perm = permsRequired[category]
      if (helpers.hasPermission(perms, category, perm)) {
        return true
      }
    }
    return false
  }
}

const getters = {
  hasViewAccountPerm: state => helpers.hasPermission(state.perms, 'account', 'view'),
  hasAdminPerm: state => helpers.hasPermission(state.perms, 'admin', 'manage'),
  hasViewContactPerm: state => helpers.hasPermission(state.perms, 'contacts', 'view'),
  hasManageContactPerm: state => helpers.hasPermission(state.perms, 'contacts', 'manage'),
  hasInvoiceViewPerm: state => helpers.hasPermission(state.perms, 'invoices', 'view'),
  hasPaymentsViewPerm: state => helpers.hasPermission(state.perms, 'payments', 'view'),
  hasPaymentsManagePerm: state => helpers.hasPermission(state.perms, 'payments', 'manage'),
  hasServicesViewPerm: state => helpers.hasPermission(state.perms, 'services', 'view'),
  hasServicesManagePerm: state => helpers.hasPermission(state.perms, 'services', 'manage'),
  hasTicketsViewPerm: state => helpers.hasPermission(state.perms, 'tickets', 'view'),
  hasTicketsManagePerm: state => helpers.hasPermission(state.perms, 'tickets', 'manage'),
  isPrimaryAdmin: state => state.primary_admin,
  isSiteAdmin: state => state.siteAdmin,
  isPBAUser: state => {
    return state.cid >= 1000000
  },
  username: state => state.username,
  isLoggedIn: state =>state.loggedIn,
  hasDarkTheme: state => state.hasDarkTheme  // returns true, false, or null (if unset)
}

const mutations = {
  // eslint-disable-next-line camelcase
  [types.LOGGED_IN] (state, { username, cid, perms, site_admin }) {
    state.username = username
    state.cid = cid
    state.perms = perms
    state.loggedIn = true
    // eslint-disable-next-line camelcase
    state.siteAdmin = site_admin
  },

  [types.SET_LOGGED_IN] (state) {
    state.loggedIn = true
  },

  [types.LOGGED_OUT] (state) {
    state.username = ''
    state.roles = []
    state.loggedIn = false
    state.siteAdmin = false
  },

  // eslint-disable-next-line camelcase
  [types.SET_PROFILE] (state, { username, cid, perms, site_admin }) {
    state.username = username
    state.cid = cid
    state.perms = perms
    // eslint-disable-next-line camelcase
    state.siteAdmin = site_admin
  },

  [types.SET_USER] (state, input) {
    state.info.name = input['name']
    state.info.dob = input['dob']
    state.info.email = input['email']
    state.info.phone = input['phone']
    state.info.business = input['business']
    state.info.first_name = input['first_name']
    state.info.middle_initial = input['middle_initial']
    state.info.last_name = input['last_name']
    state.primary_admin = input['primary_admin']
    state.service_address = input['service_address']
    state.user_loaded = true
  },

  [types.SET_PERM_SCHEMA] (state, permSchema) {
    state.perm_schema = permSchema
  },

  [types.SET_PERM_CATEGORIES] (state, permCategories) {
    state.perm_categories = permCategories
  },
  [types.SAVE_AJAX_REQUEST] (state, { ajaxCallback, ajaxOptions }) {
    state.ajaxCallback = ajaxCallback
    state.ajaxOptions = ajaxOptions
  },
  [types.CLEAR_AJAX_REQUEST] (state) {
    state.ajaxCallback = undefined
    state.ajaxOptions = undefined
  },
  [types.SET_PASSWORD_WORDLIST] (state, passwordWordList) {
    state.passwordWordList = passwordWordList
  },
  [types.HAS_DARK_THEME] (state, hasTheme) {
    state.hasDarkTheme = hasTheme  // this flag matches the state of localstorage containing darkmode, i.e. it === 'dark' and may be null
  }
}

const actions = {
  /*
   * Attempt to log the user in
   */
  login  ({ state, commit, dispatch, keepNotifications}, payload) {
    return new Promise((resolve, reject) => {
      $.ajax({
        type: 'POST',
        data: JSON.stringify({
          username: payload.username,
          password: payload.password,
          store_in_cookies: true
        }),
        tryCount: 0,
        url: '/manage/api/v1.0/auth/tokens',
        contentType: 'application/json',
        success (result) {
          // Succesfully logged in
          commit(types.LOGGED_IN, {
            username: result.username,
            cid: result.cid,
            perms: result.perms,
            siteAdmin: result.site_admin
          })

          if (keepNotifications !== true ){
            commit(types.HIDE_ALL_NOTIFICATIONS)
          }
          localStorage.setItem('xm_username', result.username)
          resolve()
        },
        error (jqXHR, textStatus, errorThrown) {
          if (req.handleTimeout(jqXHR, textStatus, this)) {
            return
          } else if (jqXHR.status === 401) {
            // Invalid credentials
            commit(types.LOGGED_OUT)
            reject(jqXHR.responseJSON.title)
          } else {
            // TODO : handle error
            console.log('Error logging in: ' + textStatus + ' ' + errorThrown, jqXHR)
            reject(jqXHR.responseJSON.title)
          }
        }
      })
    })
  },
  /*
   * Will log the user out by sending a request to the server
   */
  logout ({ commit, dispatch }, router) {
    return new Promise((resolve, reject) => {
      req.delete({
        disableApiUrl: true,
        url: '/manage/api/v1.0/auth/tokens',
        success (result) {
          commit(types.LOGGED_OUT)
          commit(types.SET_NOTICES, [])
          commit(types.SHOW_LOGOUT_NOTIFICATION)
          if (result.proxy_logout === true) {
            commit(types.LOGGED_IN, {
              username: result.username,
              cid: result.cid,
              perms: result.perms,
              siteAdmin: result.site_admin
            })

            // Gonna need to do more this eventually. See:
            // https://gitlab.xmission.com/xmission/manage-frontend/issues/79
            dispatch('getProfile')
            // get notices so that staff don't accidentally bump users notifications
            dispatch('getNotices')
            router.push({ name: 'admin-proxy' })
          } else {
            router.push({ name: 'login' })
          }
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },
  updatePassword ({ commit }, { username, password, url }) {
    return new Promise((resolve, reject) => {
      req.post({
        url: '/token/update_password/' + url,
        data: {
          username: username,
          password: password
        },
        success (result) {
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },
  changePassword ({ commit }, { currentPass, newPass }) {
    return new Promise((resolve, reject) => {
      req.post({
        disableApiUrl: true,
        url: '/manage/api/v1.0/token/password_reset',
        data: {
          old_password: currentPass,
          new_password: newPass
        },
        notifications: false,
        modalNotifications: true,
        success (result) {
          return resolve(result)
        },
        error (result) {
          return reject(result)
        }
      })
    })
  },
  /*
   * Get the user's profile from the server
   */
  getProfile ({ commit }) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/user/profile',
        success (result) {
          commit(types.SET_PROFILE, result)
          commit(types.SET_LOGGED_IN)
          resolve(result)
        },
        error (result) {
          commit(types.LOGGED_OUT)
          reject(result)
        }
      })
    })
  },
  /*
   * Get the user's info from the server
   */
  getInfo ({ commit }) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/user/info',
        success (result) {
          commit(types.SET_USER, result)
          if (result.billing) {
            commit(types.SET_BILLING_ADDRESS, result.billing.billing_address)
            commit(types.SET_BILLING, result.billing)
          }
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },
  /*
   * Get the user's info from the server
   */
  saveProfile ({ commit }, profile) {
    return new Promise((resolve, reject) => {
      req.put({
        url: '/user/info',
        data: profile,
        success (result) {
          return resolve(result)
        },
        error (result) {
          return reject(result)
        }
      })
    })
  },

  getPermSchema ({ commit }) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/perms/schema',
        success (result) {
          commit(types.SET_PERM_CATEGORIES, result)
          return resolve()
        },
        error (result) {
          return reject(result)
        }
      })
    })
  },

  updatePasswordWordList ({ commit }) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/passgen/15',
        notifications: false,
        modalNotifications: true,
        success (result) {
          commit(types.SET_PASSWORD_WORDLIST, result['words'])
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },

  setDarkTheme({ commit }, setLocalStorage = true) {
    return new Promise((resolve, reject) => {
      $("body").addClass("darkMode").removeClass("liteMode");
      if (setLocalStorage){
        localStorage.setItem('xm-theme', 'dark');
      }
      commit(types.HAS_DARK_THEME, true);
    })
  },
  setLightTheme({ commit }, setLocalStorage = true) {
    return new Promise((resolve, reject) => {
      $("body").removeClass("darkMode").addClass("liteMode");
      if (setLocalStorage) {
        localStorage.setItem('xm-theme', 'light');
      }
      commit(types.HAS_DARK_THEME, false);
    })
  }
}

export default {
  state,
  mutations,
  actions,
  getters,
  helpers
}



// WEBPACK FOOTER //
// ./src/store/modules/user.js